import { Link } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import type { IncidentReport } from '@lmc/ts-common'
import type {
  ContributingFactorEnum,
  IncidentReportFetchByLogIdResponse
} from '@lmc/validations'
import { Form as AntdForm, Input } from 'antd'
import type { Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import StepCheckbox from '../StepCheckbox'
import StepField from '../StepField'
import { StepReveal, StepRevealGroup } from '../StepReveal'
import { StepSection, StepSeparator } from '../stepLayout'
import {
  fromDateOnlyIso,
  toDateOnlyIso,
  trimmed,
  withoutBlanks
} from '../stepValues'
import { useServiceUserWords } from '../useServiceUserWords'
import ContributingFactors from './investigation/ContributingFactors'
import DebriefFields from './investigation/DebriefFields'

const testId = 'incident-case-v2-investigation'
const FIVE_WHYS_URL =
  'https://www.england.nhs.uk/improvement-hub/wp-content/uploads/sites/44/2015/08/learning-handbook-five-whys.pdf'

type ReadCase = NonNullable<IncidentReportFetchByLogIdResponse['case']>

/** The investigation fields the read returns; ts-common's case type lacks them. */
type InvestigationCase = Partial<
  Pick<
    ReadCase,
    | 'coldDebrief'
    | 'contributingFactors'
    | 'contributingFactorsExplanation'
    | 'debriefRequired'
    | 'hotDebrief'
    | 'immediateCause'
    | 'lessonsIdentified'
    | 'serviceUserOpinion'
  >
>

type DebriefValues = {
  conductedAt?: Dayjs | null
  debriefedUsers?: Array<string>
  isRecorded?: boolean
  notes?: string
  preventativeActions?: string
}

type InvestigationValues = {
  coldDebrief?: DebriefValues
  contributingFactors?: Array<ContributingFactorEnum>
  contributingFactorsExplanation?: string
  debriefRequired?: boolean
  hotDebrief?: DebriefValues
  immediateCause?: string
  lessonsIdentified?: string
  serviceUserOpinion?: string
}

const toDebriefValues = (
  debrief: ReadCase['coldDebrief'] | undefined
): DebriefValues => ({
  conductedAt: fromDateOnlyIso(debrief?.conductedAt),
  debriefedUsers: debrief?.debriefedUsers ?? [],
  isRecorded: Boolean(debrief),
  notes: debrief?.notes ?? '',
  preventativeActions: debrief?.preventativeActions ?? ''
})

/** The debrief fields this step edits; the rest of a saved debrief is kept. */
const DEBRIEF_FIELDS = [
  'conductedAt',
  'debriefedUsers',
  'notes',
  'preventativeActions'
] as const

/**
 * A ticked debrief, sent whole because the save replaces it: what the step
 * edits over what else was saved, blank text left out.
 */
const toDebrief = (
  debrief: DebriefValues | undefined,
  saved: ReadCase['coldDebrief'] | undefined,
  hasPreventativeActions = false
) => {
  const kept = Object.fromEntries(
    Object.entries(saved ?? {}).filter(
      ([key]) => !(DEBRIEF_FIELDS as ReadonlyArray<string>).includes(key)
    )
  )

  return {
    ...kept,
    ...withoutBlanks({
      conductedAt: toDateOnlyIso(debrief?.conductedAt) ?? undefined,
      notes: trimmed(debrief?.notes),
      preventativeActions: hasPreventativeActions
        ? trimmed(debrief?.preventativeActions)
        : undefined
    }),
    debriefedUsers: debrief?.debriefedUsers ?? []
  }
}

/** The debriefs the case had saved when the step opened. */
type SavedDebriefs = Partial<
  Record<'coldDebrief' | 'hotDebrief', ReadCase['coldDebrief']>
>

/**
 * A ticked debrief is sent whole. One no longer ticked, or no longer required,
 * is sent as null to remove it only if one was saved; otherwise it is left out.
 * Blank text is sent as null, which removes it.
 */
const toStepValuesFor =
  (saved: SavedDebriefs) =>
  (values: InvestigationValues): IncidentCaseV2StepValues => {
    const debriefRequired = values.debriefRequired ?? false

    return {
      debriefRequired,
      ...(debriefRequired && values.hotDebrief?.isRecorded
        ? { hotDebrief: toDebrief(values.hotDebrief, saved.hotDebrief) }
        : saved.hotDebrief
          ? { hotDebrief: null }
          : {}),
      ...(debriefRequired && values.coldDebrief?.isRecorded
        ? {
            coldDebrief: toDebrief(values.coldDebrief, saved.coldDebrief, true)
          }
        : saved.coldDebrief
          ? { coldDebrief: null }
          : {}),
      contributingFactors: values.contributingFactors ?? [],
      contributingFactorsExplanation:
        trimmed(values.contributingFactorsExplanation) ?? null,
      immediateCause: trimmed(values.immediateCause) ?? null,
      lessonsIdentified: trimmed(values.lessonsIdentified) ?? null,
      serviceUserOpinion: trimmed(values.serviceUserOpinion) ?? null
    }
  }

/**
 * Step 7: whether a debrief is needed and the hot and cold debriefs, the root
 * cause analysis, the service user's opinion and the lessons identified.
 */
const InvestigationStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { singular: serviceUser } = useServiceUserWords()

  const saved = incidentReport.case as
    | (IncidentReport['case'] & InvestigationCase)
    | null

  const initialValues = useMemo<InvestigationValues>(
    () => ({
      coldDebrief: toDebriefValues(saved?.coldDebrief),
      contributingFactors: saved?.contributingFactors ?? [],
      contributingFactorsExplanation:
        saved?.contributingFactorsExplanation ?? '',
      debriefRequired: saved?.debriefRequired ?? false,
      hotDebrief: toDebriefValues(saved?.hotDebrief),
      immediateCause: saved?.immediateCause ?? '',
      lessonsIdentified: saved?.lessonsIdentified ?? '',
      serviceUserOpinion: saved?.serviceUserOpinion ?? ''
    }),
    [saved]
  )

  // Fixed at mount, like the form's initial values.
  const [toStepValues] = useState(() =>
    toStepValuesFor({
      coldDebrief: saved?.coldDebrief,
      hotDebrief: saved?.hotDebrief
    })
  )

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <StepSection
        title={t(incidentReportsConstants.CASE_V2_INVESTIGATION_DEBRIEFS)}
      >
        <StepRevealGroup>
          <AntdForm.Item name="debriefRequired" valuePropName="checked" noStyle>
            <StepCheckbox
              confirmUntick={{
                body: t(
                  incidentReportsConstants.CASE_V2_INVESTIGATION_DEBRIEF_NO_CONFIRM_BODY
                ),
                isNeeded: (form) =>
                  form.getFieldValue(['hotDebrief', 'isRecorded']) ||
                  form.getFieldValue(['coldDebrief', 'isRecorded']),
                onConfirm: (form) =>
                  form.setFieldsValue({
                    coldDebrief: toDebriefValues(undefined),
                    hotDebrief: toDebriefValues(undefined)
                  }),
                title: t(
                  incidentReportsConstants.CASE_V2_INVESTIGATION_DEBRIEF_NO_CONFIRM_TITLE
                )
              }}
              testId={`${testId}-debrief-required`}
            >
              {t(
                incidentReportsConstants.CASE_V2_INVESTIGATION_DEBRIEF_REQUIRED
              )}
            </StepCheckbox>
          </AntdForm.Item>
          <AntdForm.Item noStyle dependencies={['debriefRequired']}>
            {({ getFieldValue }) =>
              getFieldValue('debriefRequired') && (
                <StepReveal $gap={cssVars.spacing[200]}>
                  <DebriefFields kind="hotDebrief" testId={testId} />
                  <DebriefFields kind="coldDebrief" testId={testId} />
                </StepReveal>
              )
            }
          </AntdForm.Item>
        </StepRevealGroup>
      </StepSection>

      <StepSeparator />

      <StepSection
        title={t(
          incidentReportsConstants.CASE_V2_INVESTIGATION_ROOT_CAUSE_ANALYSIS
        )}
      >
        <StepField
          name="immediateCause"
          label={t(
            incidentReportsConstants.CASE_V2_INVESTIGATION_IMMEDIATE_CAUSE
          )}
          hint={
            <Trans
              i18nKey={
                incidentReportsConstants.CASE_V2_INVESTIGATION_IMMEDIATE_CAUSE_HINT
              }
              ns={NAMESPACE.INCIDENT_REPORTS}
              components={{
                fiveWhysLink: (
                  <FiveWhysLink
                    href={FIVE_WHYS_URL}
                    newTabLabel={t(
                      incidentReportsConstants.CASE_V2_INVESTIGATION_OPENS_IN_NEW_TAB
                    )}
                  />
                )
              }}
            />
          }
        >
          <Input.TextArea
            data-testid={`${testId}-immediate-cause`}
            autoSize={{ minRows: 3 }}
          />
        </StepField>
        <ContributingFactors testId={testId} />
        <StepField
          name="contributingFactorsExplanation"
          label={t(incidentReportsConstants.CASE_V2_INVESTIGATION_EXPLANATION)}
          hint={t(
            incidentReportsConstants.CASE_V2_INVESTIGATION_EXPLANATION_HINT
          )}
        >
          <Input.TextArea
            data-testid={`${testId}-explanation`}
            autoSize={{ minRows: 3 }}
          />
        </StepField>
        <StepField
          name="serviceUserOpinion"
          label={t(
            incidentReportsConstants.CASE_V2_INVESTIGATION_SERVICE_USER_OPINION,
            { serviceUser }
          )}
          hint={t(
            incidentReportsConstants.CASE_V2_INVESTIGATION_SERVICE_USER_OPINION_HINT,
            { serviceUser }
          )}
        >
          <Input.TextArea
            data-testid={`${testId}-service-user-opinion`}
            autoSize={{ minRows: 3 }}
          />
        </StepField>
      </StepSection>

      <StepSeparator />

      <StepSection>
        <StepField
          name="lessonsIdentified"
          label={t(
            incidentReportsConstants.CASE_V2_INVESTIGATION_LESSONS_IDENTIFIED
          )}
          hint={t(
            incidentReportsConstants.CASE_V2_INVESTIGATION_LESSONS_IDENTIFIED_HINT
          )}
        >
          <Input.TextArea
            data-testid={`${testId}-lessons-identified`}
            autoSize={{ minRows: 3 }}
          />
        </StepField>
      </StepSection>
    </IncidentCaseV2StepForm>
  )
}

const { link } = cssVars.color.text

/**
 * The legacy `a:hover`/`a:focus` colours sit outside the cascade layers and
 * beat the DS Link's own, so restate them for this link.
 */
const FiveWhysLink = styled(Link)`
  &:focus {
    color: ${link.primary.default};
  }

  &:hover {
    color: ${link.primary.hover};
  }

  &:active {
    color: ${link.primary.active};
  }
`

export default InvestigationStep
